import { BlockList, PlacementProvider } from '@zillow/cms-blocks';
import { Box, Divider } from '@zillow/constellation-10';
import { AISummary } from '@zillow/zrs-cms';
import { format } from 'date-fns';
import React from 'react';

import FooterTemplate from '../FooterTemplate/FooterTemplate';
import RelatedPosts from '../RelatedPosts/RelatedPosts';
import Tags from '../Tags/Tags';

import {
    B2CPostTemplateProps,
    SidebarWrapperProps,
} from './B2CPostTemplate.types';
import type { Contributor } from './B2CPostTemplate.types';
import ContributorsCard from './ContributorsCard/ContributorsCard/ContributorsCard';
import PostFeaturedImage from './PostFeaturedImage/PostFeaturedImage';
import PostHeader from './PostHeader/PostHeader';

export const SidebarWrapper: React.FC<SidebarWrapperProps> = ({
    children,
    sticky = false,
    top = '24px',
}) => (
    <Box
        css={{
            ...(sticky
                ? {
                      position: 'sticky',
                      alignSelf: 'flex-start',
                  }
                : {}),
            flexShrink: '0',
            marginTop: 'layout.looser',
            marginLeft: 'auto',
            marginRight: 'auto',
            width: '300px',
            lgDown: {
                width: '100%',
                marginTop: '0',
            },
            lg: {
                marginLeft: 'layout.vast.default',
                marginRight: '0',
                marginTop: '0',
            },
        }}
        style={sticky ? { top } : undefined}
    >
        {children}
    </Box>
);

const B2CPostTemplate: React.FC<B2CPostTemplateProps> = ({
    post,
    stickySidebarTop,
}) => {
    const postSidebar = post?.sidebar ? (
        <BlockList blocks={post.sidebar.blocks || []} />
    ) : null;

    const reviewersArr: Contributor[] = Array.isArray(post.meta?.reviewedBy)
        ? post.meta.reviewedBy
              .filter(
                  (r): r is Contributor =>
                      r != null && typeof r.name === 'string'
              )
              .map((r: Contributor) => ({
                  name: r.name,
                  avatar: r.avatar,
                  link: r.link,
                  role: 'reviewer' as const,
                  jobTitle: r.jobTitle,
                  expertise: r.expertise,
                  description: r.description,
              }))
        : [];
    const editorsArr: Contributor[] = Array.isArray(post.meta?.editor)
        ? post.meta.editor.map((e: Contributor) => ({
              name: e.name,
              avatar: e.avatar,
              link: e.link,
              role: 'editor' as const,
              jobTitle: e.jobTitle,
              expertise: e.expertise,
              description: e.description,
          }))
        : [];

    const contributors = {
        author: post.author && {
            name: post.author.name,
            avatar: post.author.avatar,
            link: post.author.link,
            role: 'author' as const,
            date: post.date
                ? (() => {
                      try {
                          return format(new Date(post.date), 'MMMM d, yyyy');
                      } catch {
                          return undefined;
                      }
                  })()
                : undefined,
            jobTitle: post.author.jobTitle,
            expertise: post.author.expertise,
            description: post.author.description,
        },
        reviewers: reviewersArr,
        editors: editorsArr,
    };

    return (
        <React.Fragment>
            <Box
                css={{
                    maxWidth: 'breakpoint-xl',
                    marginInline: 'auto',
                    paddingTop: { base: 'loose', lg: 'looser' },
                    paddingBottom: 'layout.looser',
                    paddingInline: 'default',
                }}
            >
                {/* All content container, including sidebar */}
                <Box>
                    <PostHeader post={post} />

                    {/* This is the main content and sidebar */}
                    <Box
                        css={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            lgDown: {
                                flexDirection: 'column',
                                gap: 'loose',
                            },
                        }}
                    >
                        {/* Main content container */}
                        <Box
                            data-epi-type="content"
                            css={{
                                flex: '1 1 0',
                                minWidth: '0',
                                maxWidth: '100%',
                            }}
                        >
                            <PostFeaturedImage post={post} />
                            {contributors && contributors.author && (
                                <Box
                                    css={{
                                        marginTop: 'default',
                                        marginBottom: 'loose',
                                        width: '100%',
                                    }}
                                >
                                    <ContributorsCard
                                        author={contributors.author}
                                        reviewers={contributors.reviewers}
                                        editors={contributors.editors}
                                    />
                                </Box>
                            )}

                            {/* Divider */}
                            <Divider css={{ marginBottom: 'loose' }} />

                            <AISummary
                                bullets={post.meta?.aiSummaryBullets}
                                paragraph={post.meta?.aiSummaryParagraph}
                                displayType={post.meta?.aiSummaryDisplayType}
                            />

                            <PlacementProvider placement="post">
                                <BlockList blocks={post.blocks || []} />
                            </PlacementProvider>
                            <Tags postTags={post.postTags} />
                        </Box>

                        {/* Sidebar container */}
                        {postSidebar && (
                            <SidebarWrapper sticky top={stickySidebarTop}>
                                {postSidebar}
                            </SidebarWrapper>
                        )}
                    </Box>
                </Box>
            </Box>

            {(post.footer || post.relatedPosts) && (
                <FooterTemplate
                    footer={post.footer}
                    relatedArticles={
                        post.relatedPosts && (
                            <RelatedPosts
                                post={{
                                    ...post,
                                    id: parseInt(post.id),
                                    relatedPosts: post.relatedPosts.map(
                                        (p) => ({
                                            ...p,
                                            id: parseInt(p.id),
                                        })
                                    ),
                                }}
                            />
                        )
                    }
                />
            )}
        </React.Fragment>
    );
};

export default B2CPostTemplate;
